import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Select,
} from '@chakra-ui/react';
import {
  SaktiEstateOption,
  SaktiFilterOptions,
  SaktiRkhFilters,
} from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiRkhFilterBarProps {
  filters: SaktiRkhFilters;
  options: SaktiFilterOptions;
  onFiltersChange: (nextFilters: SaktiRkhFilters) => void;
  onApply: () => void;
  isApplying?: boolean;
}

const SaktiRkhFilterBar: React.FC<SaktiRkhFilterBarProps> = ({
  filters,
  options,
  onFiltersChange,
  onApply,
  isApplying = false,
}) => {
  const filteredEstates = useMemo(() => {
    if (!filters.company_id) {
      return options.estates;
    }
    return options.estates.filter((estate) => estate.company_id === filters.company_id);
  }, [filters.company_id, options.estates]);

  const filteredDivisions = useMemo(() => {
    if (!filters.estate_id) {
      return [];
    }

    const selectedEstate = options.estates.find((estate) => estate.odoo_id === filters.estate_id);
    if (!selectedEstate) {
      return [];
    }

    return options.divisions.filter((division) => division.estate_id === selectedEstate.id);
  }, [filters.estate_id, options.divisions, options.estates]);

  const updateFilter = (changes: Partial<SaktiRkhFilters>) => {
    onFiltersChange({
      ...filters,
      ...changes,
    });
  };

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      p={SAKTI_UI_SIZE.cardPadding}
      borderWidth="1px"
      borderColor="gray.200"
    >
      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: '1fr 1fr 1fr 1fr auto' }}
        gap={SAKTI_UI_SIZE.filterGridGap}
      >
        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            TANGGAL
          </FormLabel>
          <Input
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            type="date"
            value={filters.date}
            onChange={(event) => updateFilter({ date: event.target.value })}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            COMPANY
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={filters.company_id ?? ''}
            placeholder="Pilih company"
            onChange={(event) =>
              updateFilter({
                company_id: event.target.value ? Number(event.target.value) : undefined,
                estate_id: undefined,
                division_id: undefined,
                page: 1,
              })
            }
          >
            {options.companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            ESTATE
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            value={filters.estate_id ?? ''}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            placeholder="Pilih estate"
            onChange={(event) =>
              updateFilter({
                estate_id: event.target.value ? Number(event.target.value) : undefined,
                division_id: undefined,
                page: 1,
              })
            }
          >
            {filteredEstates.map((estate: SaktiEstateOption) => (
              <option key={estate.id} value={estate.odoo_id}>
                {estate.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            DIVISION
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={filters.division_id ?? ''}
            placeholder="Pilih divisi"
            isDisabled={!filters.estate_id}
            onChange={(event) =>
              updateFilter({
                division_id: event.target.value ? Number(event.target.value) : undefined,
                page: 1,
              })
            }
          >
            {filteredDivisions.map((division) => (
              <option key={division.id} value={division.odoo_id}>
                {division.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button
          alignSelf="end"
          size="sm"
          h={SAKTI_UI_SIZE.controlHeight}
          w={{ base: 'full', xl: 'auto' }}
          px={5}
          isLoading={isApplying}
          loadingText="Applying"
          onClick={onApply}
          bg="customGreen.500"
          _hover={{ bg: 'customGreen.600' }}
          color="white"
          mt={{ base: 2, xl: 0 }}
        >
          Apply Filter
        </Button>
      </Grid>
    </Box>
  );
};

export default SaktiRkhFilterBar;
